import { FC, useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Paper,
  Button,
  Card,
  CardContent,
  InputAdornment,
  Alert,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  WarningAmberRounded as WarningIcon,
  CheckCircleOutlineRounded as CheckCircleIcon,
  DangerousRounded as HazardIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceDot,
  Label,
  Area,
} from "recharts";
import { AssessmentContext } from "src/context/AssessmentContext";
import {
  saveHandArmVibrationData,
  getHandArmVibrationData,
} from "src/app/logic/assessment/msd/analysisUtils";

type VibrationRiskLevel = "OK" | "CAUTION" | "HAZARD";

export const HandArmVibrationAnalysis: FC = () => {
  const { assessmentDataDto, setAssessmentDataDto } =
    useContext(AssessmentContext);

  const existingData = getHandArmVibrationData(assessmentDataDto?.msd);

  const [vibrationValue, setVibrationValue] = useState<string>(
    existingData.vibrationValue?.toString() || "",
  );
  const [exposureTime, setExposureTime] = useState<string>(
    existingData.exposureTime?.toString() || "",
  );
  const [riskLevel, setRiskLevel] = useState<VibrationRiskLevel | null>(
    existingData.riskLevel || null,
  );
  const [calculatedLimits, setCalculatedLimits] = useState<{
    caution: number;
    hazard: number;
  } | null>(
    existingData.cautionLimit && existingData.hazardLimit
      ? {
          caution: existingData.cautionLimit,
          hazard: existingData.hazardLimit,
        }
      : null,
  );

  const [chartPlotData, setChartPlotData] = useState<any[]>([]);
  const [userPointData, setUserPointData] = useState<
    { time: number; vibration: number } | undefined
  >(undefined);

  useEffect(() => {
    const data: any[] = [];
    uniqueTimeValues.forEach((t) => {
      if (t === 0) return;
      let cautionVal = 2.5 * Math.sqrt(8 / t);
      let hazardVal = 5 * Math.sqrt(8 / t);
      data.push({
        time: t,
        caution: parseFloat(cautionVal.toFixed(2)),
        hazard: parseFloat(hazardVal.toFixed(2)),
        yMaxFill: 60,
      });
    });

    data.sort((a, b) => a.time - b.time);
    setChartPlotData(data);
  }, []);

  const calculateVibrationRisk = () => {
    const vibVal = parseFloat(vibrationValue);
    const timeVal = parseFloat(exposureTime);

    if (
      isNaN(vibVal) ||
      vibVal <= 0 ||
      isNaN(timeVal) ||
      timeVal <= 0 ||
      timeVal > 24
    ) {
      setRiskLevel(null);
      setCalculatedLimits(null);
      setUserPointData(undefined);
      return;
    }

    setUserPointData({ time: Math.min(timeVal, 8.1), vibration: vibVal });

    const hazardLimit = 5 * Math.sqrt(8 / timeVal);
    const cautionLimit = 2.5 * Math.sqrt(8 / timeVal);

    const limits = {
      caution: parseFloat(cautionLimit.toFixed(2)),
      hazard: parseFloat(hazardLimit.toFixed(2)),
    };
    setCalculatedLimits(limits);

    let calculatedRiskLevel: VibrationRiskLevel;
    if (vibVal > hazardLimit) {
      calculatedRiskLevel = "HAZARD";
    } else if (vibVal > cautionLimit) {
      calculatedRiskLevel = "CAUTION";
    } else {
      calculatedRiskLevel = "OK";
    }
    setRiskLevel(calculatedRiskLevel);

    const updatedMsdData = saveHandArmVibrationData(assessmentDataDto?.msd, {
      vibrationValue: vibVal,
      exposureTime: timeVal,
      riskLevel: calculatedRiskLevel,
      cautionLimit: limits.caution,
      hazardLimit: limits.hazard,
    });

    setAssessmentDataDto({ msd: updatedMsdData });
  };

  const resetForm = () => {
    setVibrationValue("");
    setExposureTime("");
    setRiskLevel(null);
    setCalculatedLimits(null);
    setUserPointData(undefined);

    const updatedMsdData = saveHandArmVibrationData(assessmentDataDto?.msd, {
      vibrationValue: undefined,
      exposureTime: undefined,
      riskLevel: undefined,
      cautionLimit: undefined,
      hazardLimit: undefined,
    });

    setAssessmentDataDto({ msd: updatedMsdData });
  };

  useEffect(() => {
    const vibValNum = parseFloat(vibrationValue);
    const timeValNum = parseFloat(exposureTime);
    if (
      !vibrationValue ||
      !exposureTime ||
      isNaN(vibValNum) ||
      vibValNum <= 0 ||
      isNaN(timeValNum) ||
      timeValNum <= 0 ||
      timeValNum > 24
    ) {
      if (
        riskLevel !== null ||
        calculatedLimits !== null ||
        userPointData !== undefined
      ) {
        setRiskLevel(null);
        setCalculatedLimits(null);
        setUserPointData(undefined);
      }
    }
  }, [
    vibrationValue,
    exposureTime,
    riskLevel,
    calculatedLimits,
    userPointData,
  ]);

  const getRiskMessage = () => {
    if (!riskLevel) return "";
    switch (riskLevel) {
      case "HAZARD":
        return "The vibration hazard must be reduced below the hazard level or to the degree technologically and economically feasible.";
      case "CAUTION":
        return "The job remains as a 'Caution Zone Job.' Consider measures to reduce exposure or monitor health.";
      case "OK":
        return "No further steps are required based on this analysis.";
      default:
        return "";
    }
  };

  const getResultIcon = () => {
    if (!riskLevel) return null;
    switch (riskLevel) {
      case "HAZARD":
        return <HazardIcon sx={{ color: "error.main", mr: 1, fontSize: 30 }} />;
      case "CAUTION":
        return (
          <WarningIcon sx={{ color: "warning.main", mr: 1, fontSize: 30 }} />
        );
      case "OK":
        return (
          <CheckCircleIcon
            sx={{ color: "success.main", mr: 1, fontSize: 30 }}
          />
        );
      default:
        return null;
    }
  };

  const getResultColor = () => {
    if (!riskLevel) return "default";
    switch (riskLevel) {
      case "HAZARD":
        return "error.lighter";
      case "CAUTION":
        return "warning.lighter";
      case "OK":
        return "success.lighter";
      default:
        return "background.paper";
    }
  };

  const uniqueTimeValues = Array.from(
    new Set(
      [
        0.08, 0.1, 0.125, 0.15, 0.2, 0.25, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9,
        1.0, 1.25, 1.5, 1.75, 2.0, 2.25, 2.5, 2.75, 3.0, 3.5, 4.0, 4.5, 5.0,
        5.5, 6.0, 6.5, 7.0, 7.5, 8.0,
      ].sort((a, b) => a - b),
    ),
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Hand-Arm Vibration Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Use this tool to determine if a hand-arm vibration hazard exists based
        on tool vibration and exposure time. The caution limit is based on an
        8-hour energy-equivalent frequency-weighted acceleration value of 2.5
        m/s², and the hazard limit is based on 5 m/s².
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Input Fields */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              1. Tool Vibration Value
            </Typography>
            <TextField
              label="Vibration Value"
              type="number"
              value={vibrationValue}
              onChange={(e) => setVibrationValue(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">m/s²</InputAdornment>
                ),
                inputProps: { min: 0.01 },
              }}
              size="small"
              variant="outlined"
              fullWidth
              helperText="Find this from the manufacturer or measure it."
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              2. Total Daily Exposure Time
            </Typography>
            <TextField
              label="Exposure Time"
              type="number"
              value={exposureTime}
              onChange={(e) => setExposureTime(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">hours</InputAdornment>
                ),
                inputProps: { min: 0.01, max: 24 },
              }}
              size="small"
              variant="outlined"
              fullWidth
              helperText="Total hours per day the employee uses the tool."
            />
          </Paper>
        </Grid>

        {/* Buttons */}
        <Grid
          item
          xs={12}
          sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}
        >
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={calculateVibrationRisk}
              disabled={
                !vibrationValue ||
                !exposureTime ||
                parseFloat(vibrationValue) <= 0 ||
                parseFloat(exposureTime) <= 0 ||
                parseFloat(exposureTime) > 24
              }
            >
              Calculate Risk
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={resetForm}
            >
              Reset
            </Button>
          </Box>
        </Grid>

        {/* Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mt: 1, position: "relative" }}>
            <Typography variant="h6" gutterBottom textAlign="center">
              Exposure Graph
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                data={chartPlotData}
                margin={{ top: 5, right: 30, left: 20, bottom: 45 }}
              >
                <defs>
                  <pattern
                    id="hazardPattern"
                    patternUnits="userSpaceOnUse"
                    width="6"
                    height="6"
                    patternTransform="rotate(45)"
                  >
                    <line
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="6"
                      stroke="rgba(0,0,0,0.2)"
                      strokeWidth="1"
                    />
                  </pattern>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  dataKey="time"
                  name="Time"
                  unit="h"
                  domain={[0, 8]}
                  ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8]}
                  label={{
                    value: "Time (in hours)",
                    position: "insideBottom",
                    offset: -10,
                    dy: 15,
                  }}
                />
                <YAxis
                  type="number"
                  dataKey="vibration"
                  name="Vibration"
                  unit="m/s²"
                  domain={[0, 50]}
                  label={{
                    value: "Vibration value (in m/s²)",
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle" },
                    dx: -15,
                  }}
                />
                <Tooltip
                  formatter={(value: number, name: string, props: any) => {
                    if (name === "User Exposure") {
                      return [
                        `${props.payload.vibration} m/s²`,
                        `User Exposure (Time: ${props.payload.time}h)`,
                      ];
                    }
                    return [`${value} m/s²`, name];
                  }}
                />
                <Legend
                  verticalAlign="top"
                  wrapperStyle={{ paddingBottom: "10px" }}
                />

                {/* Area Fills for Zones */}
                <Area
                  type="monotone"
                  dataKey="yMaxFill"
                  fill="url(#hazardPattern)"
                  stroke="none"
                  name="Hazard Zone"
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="hazard"
                  fill="rgba(255, 193, 7, 0.4)"
                  stroke="none"
                  name="Caution Zone"
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="caution"
                  fill="rgba(76, 175, 80, 0.4)"
                  stroke="none"
                  name="OK Zone"
                  isAnimationActive={false}
                />

                {/* Lines on top of areas */}
                <Line
                  type="monotone"
                  dataKey="caution"
                  stroke="#007bff"
                  strokeWidth={2.5}
                  name="Caution Limit"
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="hazard"
                  stroke="#212529"
                  strokeWidth={2.5}
                  name="Hazard Limit"
                  dot={false}
                  isAnimationActive={false}
                />

                {userPointData && userPointData.vibration <= 50 && (
                  <ReferenceDot
                    x={userPointData.time}
                    y={userPointData.vibration}
                    r={6}
                    fill="#dc3545"
                    stroke="white"
                    ifOverflow="extendDomain"
                  >
                    <Label
                      value={`(${userPointData.time}h, ${userPointData.vibration}m/s²)`}
                      position="top"
                      fill="#dc3545"
                      fontSize="12"
                      dy={-5}
                    />
                  </ReferenceDot>
                )}
                {userPointData && userPointData.vibration > 50 && (
                  <ReferenceDot
                    x={userPointData.time}
                    y={50}
                    r={6}
                    fill="#dc3545"
                    stroke="white"
                  >
                    <Label
                      value={`(${userPointData.time}h, ${userPointData.vibration}m/s²) - Above Chart`}
                      position="top"
                      fill="#dc3545"
                      fontSize="12"
                      dy={-5}
                    />
                  </ReferenceDot>
                )}
              </LineChart>
            </ResponsiveContainer>
            <Box sx={{ pt: 1, textAlign: "center" }}>
              <Typography variant="caption" display="block">
                OK Zone: Below Blue Line. Caution Zone: Between Blue and Black
                Lines. Hazard Zone: Above Black Line.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Results Card */}
        {riskLevel && calculatedLimits && (
          <Grid item xs={12}>
            <Card
              elevation={3}
              sx={{
                mt: 2,
                bgcolor: getResultColor(),
              }}
            >
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" gutterBottom>
                      Results
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body1">
                        Entered Vibration:{" "}
                        <strong>{vibrationValue} m/s²</strong>
                      </Typography>
                      <Typography variant="body1">
                        Entered Exposure Time:{" "}
                        <strong>{exposureTime} hours</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        For {exposureTime} hours exposure:
                        <ul>
                          <li>
                            Caution if vibration &gt;{" "}
                            <strong>{calculatedLimits.caution} m/s²</strong>
                          </li>
                          <li>
                            Hazard if vibration &gt;{" "}
                            <strong>{calculatedLimits.hazard} m/s²</strong>
                          </li>
                        </ul>
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      {getResultIcon()}
                      <Typography
                        variant="h5"
                        component="div"
                        sx={{
                          fontWeight: "bold",
                          color:
                            riskLevel === "HAZARD"
                              ? "error.main"
                              : riskLevel === "CAUTION"
                                ? "warning.main"
                                : "success.main",
                        }}
                      >
                        {riskLevel}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Alert
                      severity={
                        riskLevel === "OK"
                          ? "success"
                          : riskLevel === "CAUTION"
                            ? "warning"
                            : "error"
                      }
                      sx={{ mt: 1.5 }}
                    >
                      {getRiskMessage()}
                    </Alert>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default HandArmVibrationAnalysis;
